import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { receiptAPI, formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { Search, FileText, Eye, Edit, Trash2, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { formatDateDisplay } from '../lib/dateUtils';
import BranchSelect from '../components/BranchSelect';

export default function ReceiptList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [receipts, setReceipts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
    branch_id: searchParams.get('branch_id') || '',
    page: parseInt(searchParams.get('page')) || 1
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: '', number: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await receiptAPI.list(params);
      setReceipts(res.data.receipts || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch { toast.error('Failed to load receipts'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Persist filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== '' && !(k === 'page' && v === 1)) params.set(k, v); });
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const downloadPDF = async (id, num) => {
    try {
      const res = await receiptAPI.downloadPDF(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${num}.pdf`; a.click();
      toast.success('PDF downloaded');
    } catch { toast.error('Download failed'); }
  };

  const handleDelete = async () => {
    try {
      await receiptAPI.delete(deleteDialog.id);
      toast.success('Receipt deleted');
      setDeleteDialog({ open: false, id: '', number: '' });
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="receipt-list-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Loading Receipts</h1>
          <p className="text-sm text-slate-500 font-body mt-1">{total} receipts</p>
        </div>
        <button onClick={() => navigate('/receipts/new')} className="btn-accent flex items-center gap-2" data-testid="new-receipt-btn">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> New Receipt
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); setFilters(f => ({ ...f, page: 1 })); }} className="flex flex-wrap gap-3 bg-white border border-slate-200 rounded-md p-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search by receipt #, consignor, consignee..." value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} className="form-input pl-9" data-testid="search-input" />
        </div>
        <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value, page: 1 }))}
          className="form-input w-36" data-testid="date-from" title="From" />
        <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value, page: 1 }))}
          className="form-input w-36" data-testid="date-to" title="To" />
        <BranchSelect allowAll value={filters.branch_id} onChange={(v) => setFilters(f => ({ ...f, branch_id: v, page: 1 }))} className="w-44" testId="receipt-branch-filter" />
        <button type="submit" className="btn-primary" data-testid="search-btn">Search</button>
      </form>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {loading ? <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="receipts-table">
              <thead><tr><th>S.No.</th><th>Receipt #</th><th>Date</th><th>Consignor</th><th>Consignee</th><th>Route</th><th className="text-right">Declared Value</th><th>Created By</th><th className="text-center">Actions</th></tr></thead>
              <tbody>
                {receipts.map((r, idx) => (
                  <tr key={r.receipt_id} data-testid={`receipt-row-${r.receipt_number}`}>
                    <td className="text-slate-500 text-sm">{(filters.page - 1) * 20 + idx + 1}</td>
                    <td className="font-mono font-medium text-slate-900">{r.receipt_number}</td>
                    <td className="text-slate-500 text-sm">{formatDateDisplay(r.booking_date)}</td>
                    <td className="text-sm">{r.consignor_name}</td>
                    <td className="text-sm">{r.consignee_name}</td>
                    <td className="text-xs text-slate-500">{r.origin} &rarr; {r.destination}</td>
                    <td className="text-right font-mono text-sm">Rs.{formatCurrency(r.total_declared_value)}</td>
                    <td className="text-xs text-slate-500">{r.created_by_name}</td>
                    <td>
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => navigate(`/receipts/${r.receipt_id}`)} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`view-${r.receipt_number}`}><Eye className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.5} /></button>
                        <button onClick={() => navigate(`/receipts/${r.receipt_id}/edit`)} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`edit-${r.receipt_number}`}><Edit className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.5} /></button>
                        <button onClick={() => downloadPDF(r.receipt_id, r.receipt_number)} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`pdf-${r.receipt_number}`}><FileText className="w-3.5 h-3.5 text-orange-600" strokeWidth={1.5} /></button>
                        {isAdmin && <button onClick={() => setDeleteDialog({ open: true, id: r.receipt_id, number: r.receipt_number })} className="p-1.5 hover:bg-rose-50 rounded-md" data-testid={`delete-${r.receipt_number}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" strokeWidth={1.5} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {receipts.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-12">No receipts found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-sm text-slate-500">Page {filters.page} of {pages}</p>
            <div className="flex gap-2">
              <button onClick={() => setFilters(f => ({ ...f, page: Math.max(1, f.page - 1) }))} disabled={filters.page <= 1} className="btn-secondary px-3 py-1.5 text-xs"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setFilters(f => ({ ...f, page: Math.min(pages, f.page + 1) }))} disabled={filters.page >= pages} className="btn-secondary px-3 py-1.5 text-xs"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, id: '', number: '' })}>
        <DialogContent><DialogHeader><DialogTitle>Delete Receipt</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Delete <strong>{deleteDialog.number}</strong>? This cannot be undone.</p>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteDialog({ open: false, id: '', number: '' })}>Cancel</Button><Button variant="destructive" onClick={handleDelete} data-testid="delete-confirm">Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
